import React, { useEffect, useRef } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { ChatMessage, CHAT_DELETED_LABEL, CHAT_DELETING_LABEL } from '../../hooks/types';
import type { MessageMenuTrigger } from '../../hooks/useChatUIInteractions';
import { FormatTime } from './FormatTime';
import { MessageMedia } from './MessageMedia';
import { AdReferralCard } from './AdReferralCard';
import { formatMessageText } from '../../../../utils/sanitize';
import { StatusIndicator } from './StatusIndicator';
import { QuotedMessagePreview } from './QuotedMessagePreview';
import { InstagramStoryReply, isInstagramStoryQuote } from './InstagramStoryReply';
import { useMessageCaption } from './useMessageCaption';

interface MessageBubbleProps {
    msg: ChatMessage;
    conversationName: string;
    conversationAvatar?: string;
    myAvatar?: string;
    chatPlatform?: 'whatsapp' | 'instagram';
    onScrollToMessage?: (messageId: string) => void;
    onContextMenu?: (e: MessageMenuTrigger) => void;
    isHighlighted?: boolean;
}

const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_PX = 12;
const VISUAL_MEDIA = new Set(['image', 'video', 'sticker']);
const SELF_TIMED = new Set(['location', 'pix']);

type StatusVariant = 'default' | 'light' | 'muted';

function menuTrigger(target: EventTarget | null, clientY: number): MessageMenuTrigger {
    return { preventDefault() {}, currentTarget: target, clientY };
}

/** Relógio + editado + status (fonte única dos metadados do balão). */
function MetaBits(props: {
    timestamp: ChatMessage['timestamp'];
    isEdited?: boolean;
    showEdited?: boolean;
    status?: ChatMessage['status'];
    isMe: boolean;
    statusVariant?: StatusVariant;
}) {
    const { timestamp, isEdited, showEdited = true, status, isMe, statusVariant = 'default' } = props;
    return (
        <>
            {isEdited && showEdited && <span className="italic">editado</span>}
            <FormatTime date={timestamp} />
            <StatusIndicator status={status} isMe={isMe} variant={statusVariant} />
        </>
    );
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
    msg,
    conversationName,
    conversationAvatar,
    myAvatar,
    chatPlatform = 'whatsapp',
    onScrollToMessage,
    onContextMenu,
    isHighlighted = false,
}) => {
    const isMe = msg.sender === 'me';
    const isMediaType = VISUAL_MEDIA.has(msg.type);
    const isDeleting = msg.text === CHAT_DELETING_LABEL;
    const isDeleted = !isDeleting && (msg.text || '').includes(CHAT_DELETED_LABEL);
    const showDeletedState = isDeleting || isDeleted;
    const reactions = msg.reactions ?? [];
    const hasReactions = reactions.length > 0;
    const displayCaption = useMessageCaption(msg, isMe);
    const isStoryReply = isInstagramStoryQuote(msg);
    const pressRef = useRef<{ timer: number; x: number; y: number; started: number } | null>(null);

    const clearPress = () => {
        if (!pressRef.current) return;
        window.clearTimeout(pressRef.current.timer);
        pressRef.current = null;
    };

    useEffect(() => clearPress, []);

    const openMenu = (trigger: MessageMenuTrigger) => {
        clearPress();
        onContextMenu?.(trigger);
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse' || !onContextMenu) return;
        clearPress();
        const bubble = event.currentTarget;
        const { clientX: originX, clientY } = event;
        const timer = window.setTimeout(() => {
            pressRef.current = null;
            openMenu(menuTrigger(bubble, clientY));
        }, LONG_PRESS_MS);
        pressRef.current = { timer, x: originX, y: clientY, started: performance.now() };
    };

    const handlePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
        const press = pressRef.current;
        if (!press) return;
        const held = performance.now() - press.started;
        const bubble = event.currentTarget;
        const { clientY } = event;
        clearPress();
        // iOS/PWA: toque longo cancela o ponteiro antes do timer; contextmenu não dispara.
        if (held >= LONG_PRESS_MS - 80) openMenu(menuTrigger(bubble, clientY));
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const press = pressRef.current;
        if (!press) return;
        if (Math.abs(event.clientX - press.x) > LONG_PRESS_MOVE_PX || Math.abs(event.clientY - press.y) > LONG_PRESS_MOVE_PX) {
            clearPress();
        }
    };

    const showCaption = !showDeletedState && !!displayCaption && displayCaption.toLowerCase() !== msg.type;
    const showFooterMeta = !showDeletedState && !displayCaption && !isMediaType && !SELF_TIMED.has(msg.type);
    const footerMuted = msg.type !== 'text';
    const meta = { timestamp: msg.timestamp, isEdited: msg.is_edited, status: msg.status, isMe };

    return (
        <div
            id={`msg-${msg.id}`}
            className={`flex min-w-0 max-w-full ${isMe ? 'justify-end' : 'justify-start'} ${isHighlighted ? 'bg-highlight/20 -mx-2 px-2 py-1 rounded-lg transition-colors duration-300' : 'mt-2'}`}
        >
            <div className={`flex min-w-0 max-w-[85%] flex-col sm:max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                {isStoryReply && <InstagramStoryReply msg={msg} isMe={isMe} />}
                <div className={`chat-bubble-wrap ${hasReactions ? 'chat-bubble-wrap--reactions' : ''}`}>
                    <div
                        onContextMenu={(event) => openMenu(event)}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={clearPress}
                        onPointerCancel={handlePointerCancel}
                        className={`flex min-w-0 max-w-full flex-col overflow-hidden rounded-lg shadow-xs ${hasReactions ? 'pb-2' : ''} ${isMe ? 'chat-bubble-me' : 'chat-bubble-them'}`}
                    >
                        {msg.adMetadata && <AdReferralCard adMetadata={msg.adMetadata} isFromMe={isMe} />}

                        <QuotedMessagePreview
                            msg={msg}
                            isMe={isMe}
                            conversationName={conversationName}
                            onScrollToMessage={onScrollToMessage}
                        />

                        {showDeletedState && (
                            <div className={`px-3 py-2 flex items-center gap-2 ${isMe ? 'text-white/70' : 'text-muted'}`}>
                                {isDeleting ? (
                                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0" aria-hidden="true" />
                                ) : (
                                    <AppIcon name="ban" className="w-4 h-4 opacity-70 shrink-0" aria-hidden="true" />
                                )}
                                <span className="text-body-sm italic">
                                    {isDeleting ? CHAT_DELETING_LABEL : CHAT_DELETED_LABEL}
                                </span>
                                <span className={`ml-auto flex items-center gap-1 chat-msg-time ${isMe ? 'text-white/60' : 'text-chat-time-fg'}`}>
                                    <FormatTime date={msg.timestamp} />
                                </span>
                            </div>
                        )}

                        {!showDeletedState && msg.type !== 'text' && (
                            <div className="relative">
                                <MessageMedia
                                    type={msg.type}
                                    media={msg.media}
                                    content={msg.text}
                                    isMe={isMe}
                                    senderName={isMe ? 'Você' : conversationName}
                                    senderAvatar={isMe ? myAvatar : conversationAvatar}
                                    timestamp={msg.timestamp}
                                    status={msg.status}
                                    chatPlatform={chatPlatform}
                                />
                                {isMediaType && !displayCaption && (
                                    <div className="absolute bottom-1 right-2 flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-1.5 py-0.5 chat-msg-time">
                                        <span className="text-white/90"><FormatTime date={msg.timestamp} /></span>
                                        <StatusIndicator status={msg.status} isMe={isMe} variant="light" />
                                    </div>
                                )}
                            </div>
                        )}

                        {showCaption && (
                            <div className="px-2.5 py-1">
                                <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
                                    <span dangerouslySetInnerHTML={{ __html: formatMessageText(displayCaption!, isMe) }} />
                                    <span className={`inline-flex items-center gap-1 ml-2 float-right mt-1 chat-msg-time ${isMe ? 'text-white' : 'text-chat-time-fg'}`}>
                                        <MetaBits {...meta} showEdited={!isDeleted} />
                                    </span>
                                </p>
                            </div>
                        )}

                        {showFooterMeta && (
                            <div
                                className={`px-3 pb-1.5 ${footerMuted ? 'pt-0.5' : ''} flex justify-end items-center gap-1 ${
                                    isMe ? (footerMuted ? 'text-white/70' : 'text-white') : 'text-chat-time-fg'
                                }`}
                                style={{ fontSize: '10px' }}
                            >
                                <MetaBits {...meta} statusVariant={footerMuted && isMe ? 'light' : 'default'} />
                            </div>
                        )}
                    </div>

                    {hasReactions ? (
                        <ul className={`chat-reaction ${isMe ? 'chat-reaction--me' : 'chat-reaction--them'}`} aria-label="Reações">
                            {reactions.map((reaction, index) => (
                                <li key={`${reaction.sender}-${index}`} title={reaction.sender === 'me' ? 'Você reagiu' : 'Reagiu'}>
                                    {reaction.emoji}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
